import { type Control, Controller } from "react-hook-form";

import { clsx, Input } from "@alpacahq/ui/old";

import type { Asset } from "~/v2/api/rest";
import type { Quote } from "~/v2/api/rest/data";
import { CRYPTO_ORDER_TYPE_OPTIONS } from "~/v2/constants/cryptoOrder";
import { SelectWithSublabel } from "../../SelectWithSublabel";
import { BuySellButtons, orderTicketFrameClass, SymbolInfo } from "../shared";
import type { UseCryptoOrderTicketControllerReturn } from "./CryptoOrderTicket.controller";
import { CostSummary } from "./presentation/CostSummary";
import { QuantityInput } from "./presentation/QuantityInput";
import { QuoteData } from "./presentation/QuoteData";
import type { CryptoOrderTicketSchema } from "./schema";
import { createClampedChangeHandler } from "./utils";

interface PriceInputProps {
  name: "limitPrice" | "stopPrice";
  label: string;
  control: Control<CryptoOrderTicketSchema>;
  step: string;
  maxDecimals: number | undefined;
  isUsdPair: boolean;
  quoteCurrency: string;
}

const PriceInput = ({
  name,
  label,
  control,
  step,
  maxDecimals,
  isUsdPair,
  quoteCurrency,
}: PriceInputProps) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <Input
        label={label}
        {...field}
        onChange={createClampedChangeHandler(maxDecimals, field.onChange)}
        type="number"
        min="0"
        step={step}
        leftIcon={isUsdPair ? <span>$</span> : undefined}
        rightIcon={
          !isUsdPair ? (
            <span className="font-medium text-xs">{quoteCurrency}</span>
          ) : undefined
        }
        errorMsg={error?.message}
      />
    )}
  />
);

interface CryptoOrderTicketPresentationProps
  extends UseCryptoOrderTicketControllerReturn {
  asset: Asset | undefined;
  quote: Quote | undefined;
  onRefreshQuoteClicked: () => void;
  AssetSearch: JSX.Element;
  fill?: boolean;
}

export const CryptoOrderTicketPresentation = ({
  // Form control & submission
  control,
  onSubmit,
  onQuantityBlur,
  isSubmitDisabled,
  isSubmitLoading,
  isSubmitCompleted,

  // Price & quote
  midPrice,
  priceChange,
  quoteDisplay,
  quoteLatestUpdated,

  // Estimated values
  estimatedCost,
  estimatedQuantity,

  // Buying power
  nonMarginBuyingPower,

  // USD pair flag
  isUsdPair,

  // Order type
  orderType,
  onOrderTypeChange,

  // Quantity
  quantityType,
  onQuantityTypeChange,
  isQuantityTypeDisabled,
  isNotionalUsd,
  quantityLabel,
  quantityTypeOptions,
  quantityStep,
  quantityMaxDecimals,

  // Time in force
  timeInForce,
  onTimeInForceChange,
  timeInForceOptions,

  // Available quantity
  availableBase,
  availableQuote,

  // Currency info
  baseCurrency,
  quoteCurrency,

  // Decimal precision
  priceMaxDecimals,

  // Additional props
  asset,
  quote,
  onRefreshQuoteClicked,
  AssetSearch,
  fill,
}: CryptoOrderTicketPresentationProps) => (
  <form
    className={orderTicketFrameClass(fill)}
    onSubmit={(event) => {
      event.preventDefault();
    }}
  >
    {AssetSearch}

    <div
      className={clsx(
        "flex max-h-0 flex-col overflow-hidden transition-all duration-300",
        asset && quote && "max-h-80 gap-3",
      )}
    >
      {asset && (
        <SymbolInfo
          symbol={asset.symbol}
          name={`${asset.symbol} pair`}
          price={`${isUsdPair ? "$" : ""}${midPrice.display}`}
          priceChange={priceChange}
        />
      )}
      {quote && (
        <QuoteData
          bidDisplay={quoteDisplay.bidDisplay}
          lastDisplay={quoteDisplay.lastDisplay}
          askDisplay={quoteDisplay.askDisplay}
          valueSize={quoteDisplay.valueSize}
          lastUpdated={quoteLatestUpdated ?? ""}
          onRefreshQuoteClicked={onRefreshQuoteClicked}
        />
      )}
    </div>

    <QuantityInput
      control={control}
      isQuantityTypeDisabled={isQuantityTypeDisabled}
      isNotionalUsd={isNotionalUsd}
      quantityType={quantityType}
      quantityLabel={quantityLabel}
      quantityTypeOptions={quantityTypeOptions}
      quantityStep={quantityStep}
      quantityMaxDecimals={quantityMaxDecimals}
      onQuantityTypeChange={onQuantityTypeChange}
      onQuantityBlur={onQuantityBlur}
      availableBase={availableBase}
      availableQuote={availableQuote}
      baseCurrency={baseCurrency}
      quoteCurrency={quoteCurrency}
      isUsdPair={isUsdPair}
    />

    {orderType === "limit" && (
      <PriceInput
        name="limitPrice"
        label="Limit Price"
        control={control}
        step={asset?.price_increment ?? "any"}
        maxDecimals={priceMaxDecimals}
        isUsdPair={isUsdPair}
        quoteCurrency={quoteCurrency}
      />
    )}

    {orderType === "stop_limit" && (
      <div className="flex flex-col gap-2">
        <PriceInput
          name="stopPrice"
          label="Stop Price"
          control={control}
          step={asset?.price_increment ?? "any"}
          maxDecimals={priceMaxDecimals}
          isUsdPair={isUsdPair}
          quoteCurrency={quoteCurrency}
        />
        <PriceInput
          name="limitPrice"
          label="Limit Price"
          control={control}
          step={asset?.price_increment ?? "any"}
          maxDecimals={priceMaxDecimals}
          isUsdPair={isUsdPair}
          quoteCurrency={quoteCurrency}
        />
      </div>
    )}

    <SelectWithSublabel
      label="Order Type"
      value={orderType}
      onChange={onOrderTypeChange}
      options={CRYPTO_ORDER_TYPE_OPTIONS}
    />

    <SelectWithSublabel
      label="Time in Force"
      value={timeInForce}
      onChange={onTimeInForceChange}
      options={timeInForceOptions}
    />

    <CostSummary
      estimatedCost={estimatedCost}
      estimatedQuantity={estimatedQuantity}
      baseCurrency={baseCurrency}
      quoteCurrency={quoteCurrency}
      minOrderSize={asset?.min_order_size}
      nonMarginBuyingPower={nonMarginBuyingPower}
      isUsdPair={isUsdPair}
    />

    <BuySellButtons
      onBuyClicked={onSubmit("buy")}
      onSellClicked={onSubmit("sell")}
      isDisabled={isSubmitDisabled}
      isLoading={isSubmitLoading}
      isSubmitted={isSubmitCompleted}
      buyLabel={`Buy ${baseCurrency}`}
      sellLabel={`Sell ${baseCurrency}`}
    />
  </form>
);
